import { Anchor, Stack, Text, Title } from "@mantine/core"

import {
  CommunicationStudioClasses,
  CUSTOMISATION_CATEGORIES,
  TEXT_CONTENT_TYPE,
} from "@tfg/types/constants/communication-studio.constant"
import {
  ComponentContent,
  ContentBlock,
  TextContentJSON,
} from "@tfg/types/schemas/communication-studio/components"
import { TextTransform } from "@tfg/types/schemas/communication-studio/cssStylesSchema"
import {
  contentVerticalSpacingCSSVariable,
  headerFontFamilyCSSVariable,
  headerFontWeightCSSVariable,
  headerLineHeightCSSVariable,
  headerTextDecorationCSSVariable,
  textColorCssVariable,
  textFontSizeCSSVariable,
  textFontWeightCSSVariable,
  titleColorCSSVariable,
} from "@tfg/types/schemas/communication-studio/cssVariables"
import { ProductSheetLanguageSchema } from "@tfg/types/schemas/languages"
import { StylingParameters } from "@tfg/types/types/communication-studio/styling-parameters.types"

import { useGetStylingParametersContext } from "../context/stylingParameters/useGetStylingParametersContext"
import { useGetTranslationsContext } from "../context/translationsContext/useGetTranslationsContext"
import { DesignStudioParametersToolkit } from "../utils/designStudioParametersToolkit"
import { isTextContentJSONArray } from "../utils/utils"

type TextParagraphProps = {
  content: ComponentContent
}

const renderParagraph = (
  paragraph: TextContentJSON[],
  state: { index: number },
  stylingParameters: StylingParameters
) => {
  const result = []
  while (
    state.index < paragraph.length &&
    paragraph[state.index].type !== "title"
  ) {
    switch (paragraph[state.index].type) {
      case TEXT_CONTENT_TYPE.TEXT:
        result.push(
          <Text
            component="span"
            key={`text-${state.index}`}
            fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontWeightCSSVariable
            )}
            c={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textColorCssVariable
            )}
            fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontSizeCSSVariable
            )}
            style={DesignStudioParametersToolkit.spreadStylingParameters({
              category: CUSTOMISATION_CATEGORIES.GLOBAL,
              stylingParameters,
              stylingParamKeys: ["letterSpacing"],
            })}
          >
            {paragraph[state.index].text}
          </Text>
        )
        break
      case TEXT_CONTENT_TYPE.STRONG:
        result.push(
          <strong
            key={`strong-${state.index}`}
            style={{
              fontSize: DesignStudioParametersToolkit.getCssVariableLiteralName(
                textFontSizeCSSVariable
              ),
              ...DesignStudioParametersToolkit.spreadStylingParameters({
                category: CUSTOMISATION_CATEGORIES.GLOBAL,
                stylingParameters,
                stylingParamKeys: ["letterSpacing"],
              }),
            }}
          >
            {paragraph[state.index].text}
          </strong>
        )
        break
      case TEXT_CONTENT_TYPE.HYPERLINK:
        result.push(
          <Anchor
            href={paragraph[state.index].link}
            target="_blank"
            rel="noreferrer"
            key={`hyperlink-${state.index}`}
            className={CommunicationStudioClasses.PACKAGING_INFO_HYPER_LINK}
            c={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textColorCssVariable
            )}
            style={DesignStudioParametersToolkit.spreadStylingParameters({
              category: CUSTOMISATION_CATEGORIES.GLOBAL,
              stylingParameters,
              stylingParamKeys: ["letterSpacing"],
            })}
          >
            {paragraph[state.index].text}
          </Anchor>
        )
        break
      default:
        result.push(null)
        break
    }
    state.index++
  }
  return result
}

export const renderTextParagraphContent = (
  textContent: TextContentJSON[],
  index: number,
  stylingParameters: StylingParameters
) => {
  const result = []
  const state = { index: 0 }
  while (state.index < textContent.length) {
    switch (textContent[state.index].type) {
      case TEXT_CONTENT_TYPE.TEXT:
        result.push(
          <Text
            key={`paragraph-${index}-${state.index}`}
            style={DesignStudioParametersToolkit.spreadStylingParameters({
              category: CUSTOMISATION_CATEGORIES.GLOBAL,
              stylingParameters,
              stylingParamKeys: ["letterSpacing"],
            })}
            fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontSizeCSSVariable
            )}
            fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontWeightCSSVariable
            )}
            c={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textColorCssVariable
            )}
          >
            {renderParagraph(textContent, state, stylingParameters)}
          </Text>
        )
        break
      case TEXT_CONTENT_TYPE.TITLE:
        result.push(
          <Title
            key={`title-${index}-${state.index}`}
            order={3}
            style={DesignStudioParametersToolkit.spreadStylingParameters({
              category: CUSTOMISATION_CATEGORIES.GLOBAL,
              stylingParameters,
              stylingParamKeys: ["letterSpacing", "lineHeight"],
            })}
            td={DesignStudioParametersToolkit.getCssVariableLiteralName(
              headerTextDecorationCSSVariable
            )}
            ff={`${DesignStudioParametersToolkit.getCssVariableLiteralName(
              headerFontFamilyCSSVariable
            )}, ${DesignStudioParametersToolkit.extractFontFamily(stylingParameters)}
           `}
            fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
              headerFontWeightCSSVariable
            )}
            tt={
              DesignStudioParametersToolkit.extractValueFromCssVariable(
                stylingParameters,
                CUSTOMISATION_CATEGORIES.GLOBAL,
                "headerTextTransform"
              ) as TextTransform | undefined
            }
            lh={DesignStudioParametersToolkit.getCssVariableLiteralName(
              headerLineHeightCSSVariable
            )}
            c={DesignStudioParametersToolkit.getCssVariableLiteralName(
              titleColorCSSVariable
            )}
          >
            {textContent[state.index].text}
          </Title>
        )
        break
      default:
        result.push(null)
        break
    }
    state.index++
  }
  return result
}

export function useGetContentInLanguage(
  content: ComponentContent
): ContentBlock[] | undefined {
  const { currentLanguage } = useGetTranslationsContext()
  const lang = ProductSheetLanguageSchema.parse(currentLanguage.toUpperCase())
  if (!content || !content[lang]) return undefined

  return content[lang]
}

export const TextParagraph = ({ content }: TextParagraphProps) => {
  const contentBlocks: ContentBlock[] | undefined =
    useGetContentInLanguage(content)
  const { stylingParameters } = useGetStylingParametersContext()
  if (!stylingParameters) return null
  if (!contentBlocks) return null
  return (
    <Stack
      justify="left"
      align="left"
      gap={DesignStudioParametersToolkit.getCssVariableLiteralName(
        contentVerticalSpacingCSSVariable
      )}
      c={DesignStudioParametersToolkit.getCssVariableLiteralName(
        textColorCssVariable
      )}
    >
      {contentBlocks.map((block, index) => {
        // Safely check if textContent is an array of TextContentJSON
        if (!isTextContentJSONArray(block.textContent)) {
          console.warn(
            "TextParagraph: textContent is not a TextContentJSON array, skipping block"
          )
          return null
        }

        const textContent = block.textContent

        return (
          <div key={`paragraph-${index}`}>
            {renderTextParagraphContent(textContent, index, stylingParameters)}
          </div>
        )
      })}
    </Stack>
  )
}
